// Regex for splitting an RTMP URL into base and stream key
const rtmpUrlRE = /(rtmp:\/\/[^\/]+\/[^\/]+)\/(.+)/;

// Splits a complete RTMP URL (with stream key) into an array with base and stream key
export function splitRtmpUrl(url) {
  var m = url.match(rtmpUrlRE);
  if (m && m.length == 3) {
    return [m[1], m[2]];
  } else {
    console.warn('Unable to parse RTMP URL into components:', url);
    return ['', ''];
  }
}

const validResolutions = [
  { width: 864, height: 480 },
  { width: 1280, height: 720 },
  { width: 1920, height: 1080 },
];

export function resolutionForBroadcast(broadcast) {
  let height;
  if (broadcast.target_video_resolution && broadcast.target_video_resolution != 'default') {
    height = parseInt(broadcast.target_video_resolution);
  } else {
    height = 720;
  }
  let width = 0;
  if (height != 0) {
    const deltas = validResolutions.map((v) => Math.abs(v.height - height)),
      bestFit = validResolutions[deltas.indexOf(Math.min(...deltas))];
    width = bestFit.width;
    height = bestFit.height;
  }
  return { width, height };
}

export default class BroadcastWithRtmpController {
  constructor(
    $uibModalInstance,
    $interpolate,
    $scope,
    $timeout,
    $window,
    $q,
    $rootScope,
    JSZip,
    BandwidthTestClient,
    StaticRTMP,
    availableBandwidth,
    broadcast,
    encoderType
  ) {
    'ngInject';
    this.$uibModalInstance = $uibModalInstance;
    this.$interpolate = $interpolate;
    this.$scope = $scope;
    this.$timeout = $timeout;
    this.$window = $window;
    this.BandwidthTestClient = BandwidthTestClient;
    this.FeatureFlags = $rootScope.account.feature_flags;
    this.broadcast = broadcast;
    this.encoderType = encoderType;
    this.availableBandwidth = availableBandwidth;

    if (this.encoderType == 'TriCaster') {
      this.activeTab = 'tricaster';
    } else {
      this.activeTab = null;
    }

    this.loadingPromise = $q.all([JSZip.init()]).then((result) => {
      const j = result[0];
      this.JSZip = j;
      this.selectTab(this.activeTab);
    });
  }

  selectTab(encoder) {
    this.activeTab = encoder;

    const { width, height } = resolutionForBroadcast(this.broadcast),
      frameRate = this._getFramerate(),
      codec = this._getCodec(height),
      { videoBitrate, audioBitrate, minVideoBitrate, maxVideoBitrate } = this._getBitrates(
        this.availableBandwidth,
        height,
        frameRate
      ),
      rtmpUrl = this.broadcast.rtmp_ingestion_url,
      params = { width, height, videoBitrate, audioBitrate, rtmpUrl, codec, frameRate };

    this.minVideoBitrate = minVideoBitrate;
    this.maxVideoBitrate = maxVideoBitrate;

    switch (this.activeTab) {
      case 'tricaster': // fallthrough
      case 'fmle':
        this._constructTriCasterFMLE(params);
        break;
      case 'obs':
        this._constructObs(params);
        break;
      case 'wirecast':
        this._constructWirecast({ videoBitrate, audioBitrate });
        break;
      default:
        this._constructGeneric(params);
        break;
    }
  }

  _constructTriCasterFMLE(params) {
    ['width', 'height', 'rtmpUrl'].forEach((p) => (this[p] = params[p]));
    this.videoBitrateKbps = params.videoBitrateKbps = Math.round(params.videoBitrate / 1000);
    this.audioBitrateKbps = params.audioBitrateKbps = Math.round(params.audioBitrate / 1000);
    const parts = splitRtmpUrl(params.rtmpUrl);
    this.rtmpUrl = params.rtmpUrl = parts[0];
    this.streamKey = params.streamKey = parts[1];
    this.streamName = params.streamName = this.broadcast.name;

    this.xmlDownloadName = this.encoderType == 'TriCaster' ? 'tricaster.xml' : 'fmle.xml';
    this.xmlUrl = `${INTEGRATIONS_ROOT}/${this.xmlDownloadName}?height=${params.height}&width=${
      params.width
    }&video_bitrate_kbps=${params.videoBitrateKbps}&audio_bitrate_kbps=${
      params.audioBitrateKbps
    }&rtmp_url=${encodeURIComponent(params.rtmpUrl)}&stream_key=${encodeURIComponent(
      params.streamKey
    )}&stream_name=${encodeURIComponent(params.streamName)}`;
    if (this.encoderType == 'TriCaster') {
      window.location.replace(this.xmlUrl);
    }
  }

  _constructObs(params) {
    params.streamName = this.broadcast.channel_id;
    params.audioBitrateKbps = Math.round(params.audioBitrate / 1000);
    const parts = splitRtmpUrl(params.rtmpUrl);
    params.rtmpUrl = parts[0];
    params.streamKey = parts[1];
    const basicInfo = this.$interpolate(require('./obs-basic-template.ini'))(params),
      serviceInfo = {
        type: 'rtmp_custom',
        settings: {
          key: params.streamKey,
          server: params.rtmpUrl,
        },
      },
      streamEncoderInfo = {
        bitrate: Math.round(params.videoBitrate / 1000),
        keyint_sec: 2,
        profile: 'high',
        rate_control: 'CBR',
        use_bufsize: false,
        vfr: false,
      },
      zip = new this.JSZip(),
      folder = zip.folder(this.broadcast.channel_id);
    folder.file('basic.ini', basicInfo);
    folder.file('service.json', JSON.stringify(serviceInfo));
    folder.file('streamEncoder.json', JSON.stringify(streamEncoderInfo));
    zip.generateAsync({ type: 'blob' }).then((b) => {
      this.$scope.$apply(() => {
        this.zipUrl = this.$window.URL.createObjectURL(b);
        this.zipDownloadName = this.broadcast.channel_id;
      });
    });
  }

  _constructGeneric(params) {
    ['width', 'height', 'rtmpUrl', 'codec', 'frameRate'].forEach((p) => (this[p] = params[p]));
    this.videoBitrateKbps = Math.round(params.videoBitrate / 1000);
    this.audioBitrateKbps = Math.round(params.audioBitrate / 1000);
    this.streamName = this.broadcast.name;
    const parts = splitRtmpUrl(this.rtmpUrl);
    this.rtmpUrl = parts[0];
    this.streamKey = parts[1];
  }

  _constructWirecast(params) {
    // These are meant to match https://github.com/boxcast/origin_server/blob/master/stream/variant.go#L4-L6
    // and https://github.com/boxcast/boxcast_api/blob/master/app/controllers/integrations/wirecast_controller.rb#L10-L136.
    const defaultBitrates = {
      480: 1128000, // 1 Mbps video + 128 kbps audio
      720: 2692000, // 2.5 Mbps video + 192 kbps audio
      1080: 4928000, // 4.8 Mbps video + 128 kbps audio
    };
    const totalChosenBitrate = params.videoBitrate + params.audioBitrate;
    let recommendedHeight = Object.keys(defaultBitrates)
      .map((k) => parseInt(k))
      .sort((a, b) => b - a)
      .find((r) => totalChosenBitrate >= defaultBitrates[r]);
    const encodings = {
      480: 'BoxCast: 480p 3:2',
      720: 'BoxCast: 720p 16:9',
      1080: 'BoxCast: 1080p 16:9',
    };
    this.encodingSelection = encodings[recommendedHeight || 480];
    this.streamName = this.broadcast.name;
  }

  _getBitrates(bandwidth, height, frameRate) {
    let videoBitrate, audioBitrate;
    if (this.broadcast.target_video_bitrate && this.broadcast.target_video_bitrate != 'default') {
      videoBitrate = parseInt(this.broadcast.target_video_bitrate);
    }
    if (this.broadcast.target_audio_bitrate && this.broadcast.target_audio_bitrate != 'default') {
      audioBitrate = parseInt(this.broadcast.target_audio_bitrate);
    }
    return this.BandwidthTestClient.getBitrates(
      bandwidth,
      videoBitrate,
      audioBitrate,
      height,
      frameRate
    );
  }

  _getCodec(height) {
    // NOTE that RTMP doesn't support UHD resolutions so they're not accounted for here.
    switch (height) {
      case 1080:
        return 'h.264, high 10 profile, level 4.2';
      case 720:
        return 'h.264, high profile, level 4';
      default:
        return 'h.264, main profile, level 3.1';
    }
  }

  _getFramerate() {
    if (this.FeatureFlags.high_frame_rate && this.broadcast.target_high_frame_rate == 'default') {
      return 60;
    } else if (this.broadcast.target_high_frame_rate === true) {
      return 60;
    } else {
      return 30;
    }
  }

  cancel() {
    this.$uibModalInstance.dismiss('cancel');
  }
}



// WEBPACK FOOTER //
// ./app/pages/events/broadcast-with-rtmp-controller.js